import { jsx } from "react/jsx-runtime";
import { useSensor, MouseSensor, TouchSensor, useSensors, DndContext, pointerWithin } from "@dnd-kit/core";
import { differenceInDays, set, add, isEqual, isPast } from "date-fns";
import { COLUMN_MARGIN } from "../appointment-schedule.constants.js";
import { AppointmentErrorCode } from "../appointment-schedule.types.js";
import { getTimeSlotDiference } from "../components/appointment/appointment.helpers.js";
import { useSaveAppointment } from "../hooks/useSaveAppointment.js";
import "@tanstack/react-query";
import "./appointments-provider.js";
import "react";
import "../helpers/common.js";
import "date-fns/locale";
import "fast-memoize";
import "../helpers/getViewTimePeriod.js";
import "../helpers/groupBy.js";
import "../../../../utils/time/time.js";
import "lodash";
import "../hooks/useAppointments.js";
import "./axios-provider.js";
import "axios";
import "../hooks/useServerSentEvent.js";
import "./working-times-provider.js";
import "../helpers/getEmptyCalendarSlots.js";
import "../helpers/fillEmptyDays.js";
import "../hooks/useWorkingTimes.js";
function isAxiosError(error) {
  return error && error.response && error.response.data && typeof error.response.data.code === "string";
}
const DragAndDropProvider = ({
  children,
  headerRef,
  clinicReferenceId,
  onDropError,
  onDropSuccess
}) => {
  const { mutateAsync: updateAppointment } = useSaveAppointment();
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 300,
      distance: 0,
      tolerance: 5
    }
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 300,
      distance: 0,
      tolerance: 5
    }
  });
  const sensors = useSensors(mouseSensor, touchSensor);
  const handleDragEnd = async ({ active, delta, over }) => {
    var _a, _b, _c, _d, _e;
    const currentData = active.data.current;
    if (!currentData)
      return;
    const { start, end, name, employeeReferenceId, patient, ...rest } = currentData;
    const differenceInMinutes = getTimeSlotDiference(delta.y);
    const daysDifference = differenceInDays(
      set((_a = over == null ? void 0 : over.data.current) == null ? void 0 : _a.day, { hours: 0, minutes: 0, seconds: 0 }),
      set(start, { hours: 0, minutes: 0, seconds: 0 })
    );
    const updatedStartTime = add(start, { minutes: differenceInMinutes, days: daysDifference });
    if (isEqual(updatedStartTime, start) && ((_c = (_b = over == null ? void 0 : over.data) == null ? void 0 : _b.current) == null ? void 0 : _c.employee.referenceId) === employeeReferenceId)
      return;
    if (isPast(updatedStartTime)) {
      onDropError == null ? void 0 : onDropError(AppointmentErrorCode.APPOINTMENT_IN_THE_PAST);
      return;
    }
    const appointmentSlotAfterMove = {
      start: updatedStartTime,
      end: add(end, { minutes: differenceInMinutes, days: daysDifference }),
      practitionerReferenceId: (_e = (_d = over == null ? void 0 : over.data) == null ? void 0 : _d.current) == null ? void 0 : _e.employee.referenceId,
      clinicReferenceId,
      title: name,
      patientReferenceId: patient.referenceId,
      ...rest
    };
    const appointmentSlotBeforeMove = {
      start,
      end,
      practitionerReferenceId: employeeReferenceId,
      clinicReferenceId,
      title: name,
      patientReferenceId: patient.referenceId,
      ...rest
    };
    try {
      await updateAppointment({ data: appointmentSlotAfterMove });
      onDropSuccess == null ? void 0 : onDropSuccess(updateAppointment, appointmentSlotBeforeMove);
    } catch (e) {
      if (isAxiosError(e)) {
        onDropError == null ? void 0 : onDropError(e.response.data.code);
      }
    }
  };
  const restrictToScrollableAncestor = (_ref) => {
    var _a;
    const { draggingNodeRect, transform, scrollableAncestorRects } = _ref;
    const boundingRect = scrollableAncestorRects[0];
    if (!draggingNodeRect || !boundingRect) {
      return transform;
    }
    const value = { ...transform };
    const offset = ((_a = headerRef.current) == null ? void 0 : _a.offsetHeight) ?? 0;
    if (draggingNodeRect.top + transform.y <= boundingRect.top + offset) {
      value.y = boundingRect.top - draggingNodeRect.top + offset;
    } else if (draggingNodeRect.bottom + transform.y >= boundingRect.top + boundingRect.height) {
      value.y = boundingRect.top + boundingRect.height - draggingNodeRect.bottom;
    }
    if (draggingNodeRect.left + transform.x <= boundingRect.left) {
      value.x = boundingRect.left - draggingNodeRect.left + COLUMN_MARGIN;
    } else if (draggingNodeRect.right + transform.x >= boundingRect.left + boundingRect.width) {
      value.x = boundingRect.left + boundingRect.width - draggingNodeRect.right - COLUMN_MARGIN;
    }
    return value;
  };
  return /* @__PURE__ */ jsx(
    DndContext,
    {
      collisionDetection: pointerWithin,
      modifiers: [restrictToScrollableAncestor],
      sensors,
      autoScroll: {
        threshold: {
          x: 0,
          y: 0.25
        }
      },
      onDragEnd: handleDragEnd,
      children
    }
  );
};
export {
  DragAndDropProvider
};
